import React, { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useNoteStore } from '../../store/noteStore';
import AuthModal from './AuthModal';
import SettingsModal from './SettingsModal';

const UserMenu: React.FC = () => {
    const { user, isAuthenticated, logout, isLoading } = useAuthStore();
    const { cloudSync } = useNoteStore();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncMessage, setSyncMessage] = useState('');

    const handleLogout = async () => {
        await logout();
        setShowDropdown(false);
    };

    const handleSync = async () => {
        if (isSyncing) return;
        setIsSyncing(true);
        setSyncMessage('');
        try {
            await cloudSync();
            setSyncMessage('✓ Đã đồng bộ');
            setTimeout(() => setSyncMessage(''), 2000);
        } catch (error) {
            console.error('Sync failed:', error);
            setSyncMessage('✗ Lỗi đồng bộ');
            setTimeout(() => setSyncMessage(''), 3000);
        } finally {
            setIsSyncing(false);
        }
    };

    if (!isAuthenticated) {
        return (
            <>
                <button
                    onClick={() => setShowAuthModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-xl
                        hover:bg-red-700 active:scale-95 transition-all font-medium text-sm"
                    aria-label="Đăng nhập"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden sm:inline">Đăng nhập</span>
                </button>

                <AuthModal
                    isOpen={showAuthModal}
                    onClose={() => setShowAuthModal(false)}
                />
            </>
        );
    }

    // Authenticated user
    return (
        <div className="relative">
            <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-xl
                    hover:bg-gray-200 active:scale-95 transition-all"
                aria-label="Menu người dùng"
            >
                {/* Avatar */}
                {user?.avatarUrl ? (
                    <img
                        src={user.avatarUrl}
                        alt={user?.name || 'Avatar'}
                        className="w-8 h-8 rounded-full object-cover"
                    />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold">
                        {user?.name?.[0] || user?.email?.[0].toUpperCase() || '?'}
                    </div>
                )}
                <span className="hidden sm:inline text-gray-700 font-medium text-sm max-w-[100px] truncate">
                    {user?.name || user?.email}
                </span>
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown */}
            {showDropdown && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowDropdown(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 z-50 overflow-hidden">
                        {/* User info */}
                        <div className="px-4 py-3 border-b border-gray-100">
                            <p className="text-sm font-semibold text-gray-900 truncate">
                                {user?.name || 'Người dùng'}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                                {user?.email}
                            </p>
                        </div>

                        {/* Menu items */}
                        <div className="py-2">
                            <button
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                                onClick={() => {
                                    setShowDropdown(false);
                                    setShowSettings(true);
                                }}
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                                Cài đặt
                            </button>

                            <button
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3"
                                onClick={() => {
                                    setShowDropdown(false);
                                    handleSync();
                                }}
                                disabled={isSyncing}
                            >
                                <svg className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                {isSyncing ? 'Đang đồng bộ...' : syncMessage || 'Đồng bộ'}
                            </button>
                        </div>

                        {/* Logout */}
                        <div className="py-2 border-t border-gray-100">
                            <button
                                onClick={handleLogout}
                                disabled={isLoading}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                </svg>
                                Đăng xuất
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Settings Modal */}
            <SettingsModal
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
            />
        </div>
    );
};

export default UserMenu;

